<template>
  <VanPopup
    position="bottom"
    class="w-full h-full overflow-hidden flex flex-col"
    v-model:show="modalStore.showRichTextEditor"
    safe-area-inset-top
  >
    <ThreadsNav :show-left-logo="false" class="rich-text-editor-header">
      <!-- <template #left>
        <button
          class="btn btn-outline btn-xs"
          @click="modalStore.showRichTextEditor = false"
        >
          <i class="iconify mingcute--align-arrow-down-line"></i>
          {{ $t("Buttons.close") }}
        </button>
      </template> -->
      <template #center>
        <div class="line-clamp-1 whitespace-nowrap w-full">
          {{
            modalStore.richTextTitle.length > 10
              ? modalStore.richTextTitle.slice(0, 10) + "..."
              : modalStore.richTextTitle
          }}
        </div>
      </template>
      <template #right>
        <div
          class="flex gap-1 text-xs md:text-sm items-center rounded-full border px-2 whitespace-nowrap py-1"
        >
          <div
            :class="[
              'rounded-full w-2 h-2',
              isChanged ? 'bg-warning' : 'bg-success',
            ]"
          />
          <span class="text-xs">
            {{ $t(`User.settings${isChanged ? "Unsaved" : "Saved"}`) }}
          </span>
        </div>
      </template>
    </ThreadsNav>
    <EditorContent
      class="flex-1 overflow-auto story-editor prose prose-sm same-size"
      :style="{ marginBottom: `${keyboardHeight + 156}px` }"
      v-if="editor"
      :editor="editor"
    />
    <RichTextEditorToolbar v-if="editor" :editor="editor" @save="handleSave" />
  </VanPopup>
</template>

<script setup lang="ts">
import { useGlobalModal } from "@/stores/globalModal";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { Editor, EditorContent, EditorEvents } from "@tiptap/vue-3";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import CharacterCount from "@tiptap/extension-character-count";
// import { showConfirmDialog } from "vant";

const modalStore = useGlobalModal();

const editor = ref<Editor>();

const isChanged = ref(false);
let debounceTimer: NodeJS.Timeout | null = null;
let autoSaveTimer: NodeJS.Timeout | null = null;

watch(
  () => modalStore.showRichTextEditor,
  (val) => {
    if (val) {
      editor.value!.commands.setContent(modalStore.richText, false);
      autoSaveTimer = setInterval(autoSave, 1000);
    } else {
      if (debounceTimer) clearTimeout(debounceTimer);
      if (autoSaveTimer) clearInterval(autoSaveTimer);
    }
  }
);

const autoSave = async () => {
  if (isChanged.value && !debounceTimer) {
    await handleSave();
  }
};

const handleSave = async () => {
  await modalStore.richTextCallback!(
    modalStore.richTextType === "richText"
      ? editor.value!.getHTML()
      : editor.value!.getText()
  );
  isChanged.value = false;
};

const handleResize = () => {
  if (window.visualViewport) {
    // 计算键盘高度
    const currentHeight = window.visualViewport.height;
    const windowHeight = window.innerHeight;

    // 如果视口高度小于窗口高度，说明键盘弹出
    if (currentHeight < windowHeight) {
      keyboardHeight.value = windowHeight - currentHeight;
    } else {
      keyboardHeight.value = 0;
    }
  }
};

const keyboardHeight = ref(0);

const onUpdate = async ({ editor }: EditorEvents["update"]) => {
  if (editor.isEmpty) {
    // try {
    //   await showConfirmDialog({
    //     title: "提示",
    //     message: "确定要删除全部内容吗？",
    //   });
    // } catch {
    //   isChanged.value = false;
    //   return editor.commands.undo();
    // }
    // if (!confirm("确定要删除全部内容吗？")) {
    //   editor.commands.undo();
    //   return;
    // }
  }
  isChanged.value = true;

  if (debounceTimer) clearTimeout(debounceTimer);

  debounceTimer = setTimeout(() => {
    debounceTimer = null;
  }, 1000);
};

// 监听字符限制变化
watch(
  () => modalStore.richTextMaxLength,
  (newLimit) => {
    if (editor.value) {
      // 重新创建编辑器以应用新的字符限制
      const currentContent = editor.value.getHTML();
      editor.value.destroy();

      editor.value = new Editor({
        extensions: [
          CharacterCount.configure({
            limit: newLimit || undefined,
          }),
          Image.configure({
            HTMLAttributes: {
              crossorigin: "anonymous",
            },
          }),
          StarterKit.configure({}),
          Highlight.configure({
            HTMLAttributes: {
              class: "text-primary bg-transparent ",
            },
          }),
        ],
        content: currentContent,
        onUpdate,
      });
    }
  }
);

onMounted(() => {
  editor.value = new Editor({
    extensions: [
      CharacterCount.configure({
        limit: modalStore.richTextMaxLength || undefined,
      }),
      Image.configure({
        HTMLAttributes: {
          crossorigin: "anonymous",
        },
      }),
      StarterKit.configure({}),
      Highlight.configure({
        HTMLAttributes: {
          class: "text-primary bg-transparent ",
        },
      }),
    ],
    content: modalStore.richText,
    onUpdate,
  });

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", handleResize);
  } else {
    window.addEventListener("resize", handleResize);
  }
});

onBeforeUnmount(() => {
  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", handleResize);
  } else {
    window.removeEventListener("resize", handleResize);
  }
});
</script>
